import React, { useCallback, useState } from 'react'
import { Memecoin } from '../../../../types/reducerTypes'
import styled, { css } from 'styled-components'
import { Box } from '../../../../shared_components/UI/Box'
import { useCreateMemecoinCommentMutation } from '../../../../modules/memecoins/api'
import { selectAccount } from '../../../../modules/web3/selectors'
import useWeb3 from '../../../../hooks/useWeb3'
import { useDispatch, useSelector } from 'react-redux'
import { appendMemecoinComment } from '../../../../modules/memecoins/slice'
import { RoundedTextInput } from '../../../../shared_components/TextInput'
import { useShouldShowForThreshold } from '../../../../theme'
import { ThemedButton } from '../../../../shared_components/ThemedButton'

export const Button = styled.button<{ disabled: boolean }>`
  border-radius: 50px;
  width: 98%;
  color: var(--Midnight, var(--text-invert, #04111b));
  text-align: center;
  font-feature-settings: 'ss01' on;
  font-family: 'Space Grotesk';
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: -0.18px;
  text-transform: uppercase;
  padding: 8px;
  height: 43px;
  background: ${({ disabled }) =>
    disabled ? '#333333' : 'var(--surface-brand, #ff5740)'};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`

export const CommentContButton = styled(Box)`
  position: absolute;
  bottom: 32px;
  right: var(--Spacing-Padding-M, 16px);
  border: none;
`

const TextAreaContainer = styled(Box)`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  align-self: stretch;
  padding-bottom: 20px;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.sm}px) {
    width: 100%;
  }
`

const TextArea = styled.textarea`
  width: 100%;
  display: flex;
  outline: none;
  align-items: center;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: var(--units-12, 12px);
  border: var(--units-1, 1px) solid var(--stroke-primary, #fff);
  background: var(--surface-minimal-primary, rgba(255, 255, 255, 0.06));
  min-height: 154px;
  padding: var(--Spacing-Padding-M, 16px);
  gap: 18px;
  resize: none;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.sm}px) {
    font-size: 16px;
    width: 100%;
  }
`

function AddComment({ memecoin }: { memecoin: Memecoin }) {
  const poolAddress = memecoin.poolAddress
  const [createComment] = useCreateMemecoinCommentMutation()
  const [content, setContent] = useState('')
  const [wantsToComment, setWantsToComment] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const account = useSelector(selectAccount)
  const isConnected = !!account
  const { initWeb3 } = useWeb3()
  const dispatch = useDispatch()

  const isAtLeastMediumDisplay = useShouldShowForThreshold('compact')

  const handleChangeContent = (e) => {
    setContent(e.target.value)
  }

  const onComment = useCallback(async () => {
    try {
      if (!isConnected) {
        initWeb3()
        return
      }

      if (!content.length) return

      setIsCreating(true)
      const { comment } = await createComment({
        data: {
          content: content.trim(),
          poolAddress,
        },
      }).unwrap()

      dispatch(appendMemecoinComment(comment))
      setIsCreating(false)
      setContent('')
    } catch (error) {
      console.error(error)
    } finally {
      setIsCreating(false)
      setContent('')
    }
  }, [content, poolAddress, createComment, isConnected, initWeb3, dispatch])

  const isDisabled = (!content.length || isCreating) || !isConnected

  const handleSubmit = () => {
    if (isDisabled) return
    onComment()
  }

  if (isAtLeastMediumDisplay) {
    return (
      <TextAreaContainer>
        {wantsToComment ? (
        <TextArea
          placeholder="Leave a comment"
          value={content}
          onChange={handleChangeContent}
        />
        ) : (
          <ThemedButton
            w="100%"
            onClick={() => setWantsToComment(true)}
            $styleType="tertiary"
          >
            Add comment
          </ThemedButton>
        )}
        {wantsToComment && (
          <CommentContButton jfe>
            <ThemedButton
              $styleType={isDisabled ? 'tertiary' : 'primary'}
              disabled={isDisabled}
              onClick={onComment}
            >
              {!isConnected
                ? 'Connect'
                : isCreating
                  ? 'Commenting...'
                  : 'Comment'}
            </ThemedButton>
          </CommentContButton>
        )}
      </TextAreaContainer>
    )
  }

  return (
    <Container ac jc>
      <RoundedTextInput
        value={content}
        onChange={handleChangeContent}
        placeholder="Leave a comment"
        hasFullWidth
        icon={
          <SendButton isDisabled={isDisabled}>
            <img
              src="/img/icons/memecoins/send.svg"
              alt="Add comment"
              onClick={handleSubmit}
            />
          </SendButton>
        }
      />
    </Container>
  )
}

const Container = styled(Box)`
  width: 100vw;
  z-index: 1000;
  padding: 16px var(--Index-Grids-Gutters, 16px);
  background: var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04));
  position: fixed;
  bottom: 0;
  left: 0;
`

const SendButton = styled.div<{ isDisabled?: boolean }>`
  display: flex;
  width: var(--units-46, 46px);
  height: var(--units-46, 46px);
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 84px;
  background: var(--surface-brand, #FF0);
  cursor: pointer;

  ${({ isDisabled }) => isDisabled && css`
    cursor: not-allowed;
    background: var(--surface-minimal-primary, rgba(255, 255, 255, 0.06));
  `}
`


export default AddComment
